import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import AppContext from '../../context/AppContext';
import MapContext from '../../context/MapContext';
import MvtContext from '../../context/MvtContext';
import { HEADER_SIZE, MAIN_MENU_MIN_SIZE, MENU_INFO_OPEN_SIZE, SEARCH_RESULT_URL } from '../../manager/GlobalManager';
import useZoomMoveMapHandlers from '../../util/hooks/map/useZoomMoveMapHandlers';
import { MAP_CENTER_ICON_Z_INDEX } from '../util/ZIndexes';
import { ReactComponent as CenterIcon } from '../../assets/icons/map_ruler_center_day.svg';
import { initialPosition, initialZoom } from '../util/initialMapView';
import { applyZoomToFit, getZoomToFitBounds, popMapView } from '../util/MapManager';
import { applySubpixelMarkerPosition } from '../markers/subpixelMarkerPosition';
import { useFocusVisibility } from '../../util/hooks/map/useFocusMode';
import { isMvtTileURL } from './MvtLayerConfig';

// In layers, we don't use cache — always compute from map; otherwise debouncer gets stale bbox on move.
export function getVisibleBboxInfo(ctx, map) {
    const params = calcVisibleBboxParamsPx(map, ctx);
    return params ? calcVisibleBbox(params.topLeft, params.bottomRight) : null;
}

export function visibleMapRectPx(ctx, map) {
    const params = calcVisibleBboxParamsPx(map, ctx);
    if (!params) {
        return null;
    }
    const tl = map.latLngToContainerPoint(params.topLeft);
    const br = map.latLngToContainerPoint(params.bottomRight);

    return { left: tl.x, top: tl.y, right: br.x, bottom: br.y, cx: params.centerPx.x, cy: params.centerPx.y };
}

export function isOutsideVisibleMap({ ctx, map, latlng }) {
    if (!ctx || !map || !latlng) {
        return false;
    }
    const rect = visibleMapRectPx(ctx, map);
    if (!rect) {
        return false;
    }
    const p = map.latLngToContainerPoint(L.latLng(latlng));

    return p.x < rect.left || p.x > rect.right || p.y < rect.top || p.y > rect.bottom;
}

export function getMapCenter(mtx, hash) {
    return mtx.visibleBboxInfo?.center ?? getCenterMapLocByHash(hash);
}

applySubpixelMarkerPosition();

const CENTRE_ICON_SIZE = 24;
// deltaY of a mouse wheel tick, as ScrollZoomHandler of MapLibre
const WHEEL_TICK_DELTA = 4.000244140625;
// px of wheel per zoom level for a trackpad and for a mouse, as MapLibre
const TRACKPAD_PX_PER_ZOOM = 100;
const WHEEL_PX_PER_ZOOM = 450;
// a longer pause between wheel events starts a new gesture
const NEW_SCROLL_GAP_MS = 400;
// the part of the remaining zoom distance passed per frame
const ZOOM_EASING = 0.3;

const MAP_SPIN_COLOR = '#1976d2';

const TOP_PADDING = HEADER_SIZE;
const BOTTOM_PADDING = 0;

export function getVisibleMapPadding(ctx, margin = 0) {
    const infoBlockWidthPx = Number.parseInt(String(ctx.infoBlockWidth), 10) || 0;
    const bottomPx = ctx.globalGraph?.show ? ctx.globalGraph.size : 0;
    const leftChromePx = MAIN_MENU_MIN_SIZE + infoBlockWidthPx;

    return {
        top: TOP_PADDING + margin,
        left: leftChromePx + margin,
        right: margin,
        bottom: bottomPx + margin,
    };
}

function calcVisibleCenterPx(map, infoBlockWidthPx) {
    const containerSize = map.getSize();
    if (!containerSize?.x || !containerSize?.y) return null;
    const infoColumnWidthPx = Number.isFinite(infoBlockWidthPx) ? infoBlockWidthPx : 0;
    const leftChromeWidthPx = infoColumnWidthPx + MAIN_MENU_MIN_SIZE;
    return {
        x: leftChromeWidthPx + (containerSize.x - leftChromeWidthPx) / 2,
        y: TOP_PADDING + (containerSize.y - TOP_PADDING - BOTTOM_PADDING) / 2,
        containerSize,
    };
}

function centerPercentsForInfoBlockPx(map, infoBlockWidthPx) {
    if (!map?.getSize) return { left: '50%', top: '50%' };
    const center = calcVisibleCenterPx(map, infoBlockWidthPx);
    if (!center) return { left: '50%', top: '50%' };
    return {
        left: `${(center.x / center.containerSize.x) * 100}%`,
        top: `${(center.y / center.containerSize.y) * 100}%`,
    };
}

export function getVisibleBboxCenterPercents(map, ctx) {
    if (!ctx) {
        return { left: '50%', top: '50%' };
    }
    const infoBlockWidthPx = Number.parseInt(String(ctx.infoBlockWidth), 10);
    return centerPercentsForInfoBlockPx(map, infoBlockWidthPx);
}

export function panToVisibleCenter(map, latlng, infoBlockWidthPx) {
    if (!map || !latlng) return;
    const center = calcVisibleCenterPx(map, infoBlockWidthPx);
    if (!center) return;
    const pinPoint = map.latLngToContainerPoint(L.latLng(latlng.lat, latlng.lng ?? latlng.lon));
    map.panBy([pinPoint.x - center.x, pinPoint.y - center.y], { animate: true });
}

export function mapSpinOptionsForVisibleBbox(map, ctx, options = {}) {
    const { isInfoBlockOpen, ...rest } = options;
    const positionPercents = isInfoBlockOpen
        ? centerPercentsForInfoBlockPx(map, MENU_INFO_OPEN_SIZE)
        : getVisibleBboxCenterPercents(map, ctx);
    return {
        color: MAP_SPIN_COLOR,
        ...rest,
        ...positionPercents,
    };
}

export default function MapStateLayer() {
    const ctx = useContext(AppContext);
    const mtx = useContext(MapContext);
    const vtx = useContext(MvtContext);
    const map = useMap();
    const { pathname } = useLocation();
    const fractionalZoom = !ctx.develFeatures || !isMvtTileURL(mtx.tileURL) || vtx.mvtTweaks?.fractionalZoom !== false;

    const [zoom, setZoom] = useState(map ? map.getZoom() : 0);
    const [move, setMove] = useState(false);

    const [centerPositionPx, setCenterPositionPx] = useState(null);

    useZoomMoveMapHandlers(map, setZoom, setMove);
    useFocusVisibility();

    useEffect(() => {
        const update = () => {
            const { topLeft, bottomRight, centerPx } = calcVisibleBboxParamsPx(map, ctx) ?? {};

            const visible = calcVisibleBbox(topLeft, bottomRight);
            if (visible && !isInitialViewWithEmptyContext(visible, map, mtx.visibleBboxInfo)) {
                mtx.setVisibleBboxInfo(visible);
            }
            setCenterPositionPx(centerPx);
        };

        update();

        map.on('resize', update);
        return () => map.off('resize', update);
    }, [zoom, move, ctx.infoBlockWidth]);

    // leaflet-spin refcount guard (not the map instance): one spin(true) per busy spell, paired with one spin(false).
    const leafletSpinPairingRef = useRef(false);

    const globalMapSpinLoading = !!(ctx.processingSaveTrack || ctx.processingPoiByUrl || ctx.processingStopByUrl);
    const spinLayoutLikeOpenInfoBlock = !!(ctx.processingPoiByUrl || ctx.processingStopByUrl);

    useEffect(() => {
        if (globalMapSpinLoading && !leafletSpinPairingRef.current) {
            map.spin(true, mapSpinOptionsForVisibleBbox(map, ctx, { isInfoBlockOpen: spinLayoutLikeOpenInfoBlock }));
            leafletSpinPairingRef.current = true;
        } else if (!globalMapSpinLoading && leafletSpinPairingRef.current) {
            map.spin(false);
            leafletSpinPairingRef.current = false;
        }
    }, [globalMapSpinLoading, spinLayoutLikeOpenInfoBlock]);

    useEffect(() => {
        return () => {
            if (leafletSpinPairingRef.current) {
                map.spin(false);
                leafletSpinPairingRef.current = false;
            }
        };
    }, []);

    useEffect(() => {
        if (fractionalZoom) {
            return undefined;
        }
        const originalZoomSnap = map.options.zoomSnap;
        map.stop();
        map.options.zoomSnap = 1;
        map.setZoom(Math.trunc(map.getZoom()), { animate: false });

        return () => {
            map.options.zoomSnap = originalZoomSnap;
        };
    }, [fractionalZoom]);

    // Leaflet's zoom animation CSS-scales the MapLibre canvas as a bitmap for 250 ms, then MapLibre redraws: the map
    // and its markers jump. Moving the map by a fraction of a zoom level per frame lets MapLibre render every step.
    useEffect(() => {
        const container = map.getContainer();
        const originalStop = map._stop;
        const originalZoomIn = map.zoomIn;
        const originalZoomOut = map.zoomOut;
        const originalSetZoom = map.setZoom;
        const originalSetZoomAround = map.setZoomAround;
        let targetZoom = map.getZoom();
        let anchor = null;
        let anchorLatLng = null;
        let frame = null;

        // the point under the cursor stays where it is
        function zoomAroundAnchor(zoom) {
            const offset = anchor.subtract(map.getSize().divideBy(2));
            return map.unproject(map.project(anchorLatLng, zoom).subtract(offset), zoom);
        }

        // one animation frame: ease towards targetZoom, finish with a single zoomend
        function step() {
            const zoom = map.getZoom();
            const snappedTarget = map._limitZoom(targetZoom);
            if (!fractionalZoom || Math.abs(snappedTarget - zoom) < 0.005) {
                frame = null;
                // no setView: its viewreset drops the GridLayer tiles
                map._move(zoomAroundAnchor(snappedTarget), snappedTarget);
                map._moveEnd(true);
                return;
            }
            const next = zoom + (snappedTarget - zoom) * ZOOM_EASING;
            frame = L.Util.requestAnimFrame(step);
            // pinch: zoom event without moveend, as Leaflet TouchZoom
            map._move(zoomAroundAnchor(next), next, { pinch: true });
        }

        // ends the animation at the current zoom so another map movement can take over
        function stopZoom() {
            if (frame !== null) {
                L.Util.cancelAnimFrame(frame);
                frame = null;
                const zoom = map._limitZoom(map.getZoom());
                map._move(zoomAroundAnchor(zoom), zoom);
                map._moveEnd(true);
            }
            targetZoom = map.getZoom();
        }

        // Leaflet stops here before setView, flyTo, dragging and touch zoom.
        map._stop = function () {
            stopZoom();
            return originalStop.call(this);
        };

        // accumulates the target: wheel notches during the animation add up instead of restarting it
        function zoomBy(delta, nextAnchor) {
            if (map._animatingZoom) {
                return;
            }
            if (frame === null) {
                anchorLatLng = map.containerPointToLatLng(nextAnchor);
            } else if (!nextAnchor.equals(anchor)) {
                anchorLatLng = map.unproject(map.project(anchorLatLng).add(nextAnchor.subtract(anchor)));
            }
            // Keep the geographic anchor between frames instead of feeding rounded pixel origins back into the center.
            anchor = nextAnchor;
            if (frame === null && map._limitZoom(targetZoom) !== map.getZoom()) {
                targetZoom = map.getZoom();
            }
            // Preserve sub-snap deltas between wheel events, including after an animation finishes.
            targetZoom = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), targetZoom + delta));
            if (frame === null && map._limitZoom(targetZoom) !== map.getZoom()) {
                originalStop.call(map);
                frame = L.Util.requestAnimFrame(step);
                map._moveStart(true, false);
            }
        }

        let wheelType = null;
        let lastWheelTime = 0;

        // trackpad or mouse, as ScrollZoomHandler of MapLibre without its 40 ms timeout: a lone tick zooms as a mouse
        function classifyWheel(value, gap) {
            if (value % WHEEL_TICK_DELTA === 0) {
                return 'wheel';
            }
            if (Math.abs(value) < 4) {
                return 'trackpad';
            }
            if (gap > NEW_SCROLL_GAP_MS) {
                return null;
            }

            return wheelType ?? (Math.abs(gap * value) < 200 ? 'trackpad' : 'wheel');
        }

        // replaces Leaflet's scrollWheelZoom, disabled in OsmAndMap
        function onWheel(event) {
            L.DomEvent.stop(event);
            // getWheelDelta divides deltaY by 3 on Mac
            const value = event.deltaMode === 0 ? event.deltaY : -L.DomEvent.getWheelDelta(event);
            if (!value) {
                return;
            }
            const gap = event.timeStamp - lastWheelTime;
            lastWheelTime = event.timeStamp;
            wheelType = classifyWheel(value, gap);
            const pxPerZoom =
                wheelType !== 'trackpad' && Math.abs(value) > WHEEL_TICK_DELTA
                    ? WHEEL_PX_PER_ZOOM
                    : TRACKPAD_PX_PER_ZOOM;
            // sigmoid as MapLibre: small deltas zoom linearly, a fast flick is compressed to a level per event
            const scale = 2 / (1 + Math.exp(-Math.abs(value) / pxPerZoom));
            const delta = -Math.sign(value) * (fractionalZoom ? Math.log2(scale) : 1);
            zoomBy(delta, map.mouseEventToContainerPoint(event));
        }

        // the +/- buttons zoom around the center of the map part not covered by the side panel
        function zoomFromButton(delta, options) {
            const center = calcVisibleCenterPx(map, Number.parseInt(String(ctx.infoBlockWidth), 10));
            const point = center ? L.point(center.x, center.y) : map.getSize().divideBy(2);
            if (options?.animate === false) {
                map.setZoomAround(point, map.getZoom() + delta, options);
            } else {
                zoomBy(delta, point);
            }
            return map;
        }

        map.zoomIn = (delta, options) => zoomFromButton(delta ?? map.options.zoomDelta ?? 1, options);
        map.zoomOut = (delta, options) => zoomFromButton(-(delta ?? map.options.zoomDelta ?? 1), options);
        // keyboard +/- zooms through setZoom
        map.setZoom = (zoom, options) =>
            options?.animate === false
                ? originalSetZoom.call(map, zoom, options)
                : zoomFromButton(zoom - map.getZoom(), options);
        // double click zooms through setZoomAround
        map.setZoomAround = (latlng, zoom, options) => {
            if (options?.animate === false) {
                return originalSetZoomAround.call(map, latlng, zoom, options);
            }
            zoomBy(zoom - map.getZoom(), latlng instanceof L.Point ? latlng : map.latLngToContainerPoint(latlng));
            return map;
        };
        L.DomEvent.on(container, 'wheel', onWheel);

        return () => {
            L.DomEvent.off(container, 'wheel', onWheel);
            map._stop = originalStop;
            map.zoomIn = originalZoomIn;
            map.zoomOut = originalZoomOut;
            map.setZoom = originalSetZoom;
            map.setZoomAround = originalSetZoomAround;
            stopZoom();
        };
    }, [ctx.infoBlockWidth, fractionalZoom]);

    // Central zoom-to-fit handler driven by useZoomToFit.
    useEffect(() => {
        if (!mtx.zoomToFitRequest) return;
        const bounds = getZoomToFitBounds({ ...mtx.zoomToFitRequest, ctx });
        if (bounds) {
            applyZoomToFit({ map, mtx, bounds });
            mtx.setZoomToFitRequest(null);
        }
    }, [mtx.zoomToFitRequest, ctx.favorites?.mapObjs, ctx.gpxFiles]);

    // Restores a saved view
    useEffect(() => {
        const request = mtx.mapViewStackRequest;
        if (!request) return;
        if (request.action === 'pop') {
            popMapView({ map, mtx, key: request.key });
        }
        mtx.setMapViewStackRequest(null);
    }, [mtx.mapViewStackRequest]);

    useEffect(() => {
        const sync = () => {
            if (globalMapSpinLoading && map._spinner?.el) {
                const pos = mapSpinOptionsForVisibleBbox(map, ctx, {
                    isInfoBlockOpen: spinLayoutLikeOpenInfoBlock,
                });
                map._spinner.el.style.left = pos.left;
                map._spinner.el.style.top = pos.top;
            }
        };
        sync();
        map.on('resize', sync);
        return () => map.off('resize', sync);
    }, [globalMapSpinLoading, spinLayoutLikeOpenInfoBlock, ctx.infoBlockWidth]);

    if (!pathname.includes(SEARCH_RESULT_URL) || !mtx.visibleBboxInfo?.center || !centerPositionPx) {
        return null;
    }

    return (
        <div
            style={{
                position: 'absolute',
                left: centerPositionPx.x,
                top: centerPositionPx.y,
                transform: 'translate(-50%, -50%)',
                zIndex: MAP_CENTER_ICON_Z_INDEX,
                pointerEvents: 'none',
                width: CENTRE_ICON_SIZE,
                height: CENTRE_ICON_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0,
            }}
        >
            <CenterIcon
                width={CENTRE_ICON_SIZE}
                height={CENTRE_ICON_SIZE}
                style={{ display: 'block', flexShrink: 0 }}
            />
        </div>
    );
}

function isInitialViewWithEmptyContext(visible, map, visibleBboxInfo) {
    return (
        !visibleBboxInfo &&
        map.getZoom() === initialZoom &&
        Math.abs(visible.center.lat - initialPosition[0]) < 0.01 &&
        Math.abs(visible.center.lng - initialPosition[1]) < 0.01
    );
}

function calcVisibleBboxParamsPx(map, ctx) {
    if (!map?.getSize) {
        return null;
    }
    const containerSize = map.getSize();
    if (!containerSize?.x || !containerSize.y) {
        return null;
    }
    const menuOffset = Number.parseInt(ctx.infoBlockWidth, 10) + MAIN_MENU_MIN_SIZE;

    const x = menuOffset + (containerSize.x - menuOffset) / 2;
    const y = TOP_PADDING + (containerSize.y - TOP_PADDING - BOTTOM_PADDING) / 2;

    const topLeft = map.containerPointToLatLng([menuOffset, TOP_PADDING]);
    const bottomRight = map.containerPointToLatLng([containerSize.x, containerSize.y - BOTTOM_PADDING]);

    if (!topLeft || !bottomRight) {
        return null;
    }

    return { topLeft, bottomRight, centerPx: { x, y } };
}

function calcVisibleBbox(topLeft, bottomRight) {
    if (!topLeft || !bottomRight) {
        return null;
    }

    const bounds = L.latLngBounds(topLeft, bottomRight);
    const center = bounds.getCenter();

    return {
        bounds: L.latLngBounds(
            [roundTo(bounds.getSouth()), roundTo(bounds.getWest())],
            [roundTo(bounds.getNorth()), roundTo(bounds.getEast())]
        ),
        center: {
            lat: roundTo(center.lat, 4),
            lng: roundTo(center.lng, 4),
        },
    };
}

function roundTo(num, precision = 3) {
    return Number.parseFloat(Number(num).toFixed(precision));
}

function getCenterMapLocByHash(hash) {
    const parts = hash.split('/').slice(1);
    if (parts.length === 2) {
        const [lat, lng] = parts.map(Number.parseFloat);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            return {
                lat: lat,
                lng: lng,
            };
        }
    }
    return null;
}
